<script setup>
/* global axios */
// Box de conversão (Meta CAPI) no ticket — padrão dos boxes do fork
// (ConversationScheduledBox). Mostra a origem do anúncio (Click-to-WhatsApp) e o
// atendente escolhe QUAL evento devolver pra Meta (Lead, Contato, Agendamento,
// Cadastro, Compra com valor, etc.) ou marca "Não converteu". Só aparece se a
// conta configurou (gateado no ContactPanel).
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import CapiEventHistory from './CapiEventHistory.vue';
import CapiEventFields from './CapiEventFields.vue';
import { useCapiEventValue } from './composables/useCapiEventValue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const route = useRoute();
const accountId = route.params.accountId;
const base = computed(
  () =>
    `/api/v1/accounts/${accountId}/astrachat/conversations/${props.conversationId}/meta_capi`
);

// eventos padrão da Meta úteis no funil de WhatsApp. `value` = aceita valor/moeda.
const availableEvents = ref([]);

const loading = ref(true);
const referral = ref({});
const events = ref([]);
const sending = ref(false);
const notConverting = ref(false);
const selected = ref('Lead');
const value = ref('');

const fromAd = computed(
  () => referral.value?.ctwa_clid || referral.value?.source_id
);
const selectedMeta = computed(() =>
  availableEvents.value.find(e => e.name === selected.value)
);
const supportsValue = computed(() => selectedMeta.value?.value || false);
const { requiresValue, invalidValue, valueParams } = useCapiEventValue(
  selected,
  value,
  supportsValue
);

const load = async () => {
  loading.value = true;
  try {
    const { data } = await axios.get(base.value);
    const d = data.data || {};
    referral.value = d.referral || {};
    availableEvents.value = d.available_events || [];
    events.value = d.events || [];
    selected.value = availableEvents.value[0]?.name || '';
  } catch (e) {
    referral.value = {};
    events.value = [];
  } finally {
    loading.value = false;
  }
};

const post = async (eventName, extra = {}) => {
  try {
    const { data } = await axios.post(`${base.value}/send`, {
      event_name: eventName,
      ...extra,
    });
    events.value = data.events || events.value;
    if (data.ok === false) {
      useAlert(t('META_CAPI.BOX.SEND_ERROR', { error: data.error || '' }));
    } else {
      useAlert(t('META_CAPI.BOX.SENT'));
    }
  } catch (e) {
    events.value = e.response?.data?.events || events.value;
    useAlert(
      e.response?.data?.error || t('META_CAPI.BOX.SEND_ERROR', { error: '' })
    );
  }
};

const sendEvent = async () => {
  sending.value = true;
  await post(selected.value, valueParams.value);
  value.value = '';
  sending.value = false;
};

const markNotConverted = async () => {
  notConverting.value = true;
  await post('NotConverted');
  notConverting.value = false;
};

const retryEvent = async eventId => {
  try {
    await axios.post(`${base.value}/events/${eventId}/retry`);
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        t('META_CAPI.BOX.SEND_ERROR', { error: '' })
    );
  } finally {
    await load();
  }
};

watch(() => props.conversationId, load, { immediate: true });
</script>

<template>
  <div class="flex flex-col gap-2 px-3.5 pt-2.5 pb-3.5">
    <div v-if="loading" class="text-xs text-n-slate-10">
      {{ t('META_CAPI.BOX.LOADING') }}
    </div>
    <template v-else>
      <!-- origem do anúncio (Click-to-WhatsApp) -->
      <div
        v-if="fromAd"
        class="flex items-start gap-2 p-1.5 rounded-lg bg-n-alpha-1"
      >
        <span
          class="i-lucide-megaphone size-3.5 mt-0.5 text-n-teal-11 shrink-0"
        />
        <div class="min-w-0">
          <div class="text-sm text-n-slate-12">
            {{ t('META_CAPI.BOX.FROM_AD') }}
          </div>
          <div
            v-if="referral.headline"
            class="text-[0.6875rem] text-n-slate-10 truncate"
          >
            {{ referral.headline }}
          </div>
          <div class="text-[0.6875rem] text-n-slate-10 truncate">
            {{
              referral.source_id
                ? `${t('META_CAPI.BOX.AD_ID')} ${referral.source_id}`
                : referral.ctwa_clid
            }}
          </div>
        </div>
      </div>
      <div v-else class="text-xs text-n-slate-10">
        {{ t('META_CAPI.BOX.NO_AD') }}
      </div>

      <CapiEventFields
        v-model:selected="selected"
        v-model:value="value"
        :options="availableEvents"
        :supports-value="supportsValue"
        :requires-value="requiresValue"
        :invalid-value="invalidValue"
      />

      <button
        class="w-full px-3 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="sending || invalidValue || !selected"
        @click="sendEvent"
      >
        {{
          sending ? t('META_CAPI.BOX.SENDING') : t('META_CAPI.BOX.SEND_EVENT')
        }}
      </button>

      <button
        class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-transparent hover:bg-n-alpha-1 hover:text-n-ruby-11 disabled:opacity-50"
        :disabled="notConverting"
        @click="markNotConverted"
      >
        {{ t('META_CAPI.BOX.NOT_CONVERTED_BTN') }}
      </button>

      <CapiEventHistory
        :events="events"
        :catalog="availableEvents"
        @retry="retryEvent"
      />
    </template>
  </div>
</template>
